import { AttackPattern, PatternState } from '../AttackPattern.js';
import { ARENA } from '../../../../config/constants.js';

/**
 * Unmade Ground (The Unmade King's signature) — the King deletes the floor
 * itself. The arena is cut into a grid of cells; a random third of them
 * glitch through the telegraph and then become VOID — standing in deleted
 * space takes steady ticks, so the fight's geography suddenly has holes in
 * it and every other pattern must be dodged around them. The player's own
 * cell is never chosen at roll time (the deletion is a maze, not a snipe),
 * and at phase 2+ the void re-rolls once mid-pattern — reality refusing to
 * stay decided.
 */
export class UnmadeGround extends AttackPattern {
  constructor({ minPhase = 0, config }) {
    super({ name: 'Unmade Ground', telegraphDuration: 1.3, activeDuration: 3.2, minPhase });
    this.config = config;
  }

  onBegin(_boss, player) {
    this._cellW = ARENA.width / this.config.cols;
    this._cellH = ARENA.height / this.config.rows;
    this._elapsed = 0;
    this._rerolled = false;
    this._tickTimer = 0;
    this._rollVoid(player);
  }

  _cellIndexAt(x, y) {
    const col = Math.min(this.config.cols - 1, Math.max(0, Math.floor((x - ARENA.x) / this._cellW)));
    const row = Math.min(this.config.rows - 1, Math.max(0, Math.floor((y - ARENA.y) / this._cellH)));
    return row * this.config.cols + col;
  }

  _rollVoid(player) {
    const total = this.config.cols * this.config.rows;
    const playerCell = this._cellIndexAt(player.x, player.y);
    const target = Math.round(total * this.config.voidFraction);
    this._void = new Set();
    while (this._void.size < target) {
      const cell = Math.floor(Math.random() * total);
      if (cell !== playerCell) this._void.add(cell);
    }
  }

  onActiveTick(dt, boss, player, _pool) {
    this._elapsed += dt;
    this._tickTimer -= dt;

    if (!this._rerolled && boss.phase >= 2 && this._elapsed >= 1.6) {
      this._rerolled = true;
      this._rollVoid(player);
    }

    if (this._tickTimer <= 0 && !player.isInvincible
      && this._void.has(this._cellIndexAt(player.x, player.y))) {
      player.takeDamage(this.config.tickDamage);
      this._tickTimer = 0.45;
    }
  }

  _cells() {
    const cells = [];
    for (const index of this._void) {
      const col = index % this.config.cols;
      const row = Math.floor(index / this.config.cols);
      cells.push({ x: ARENA.x + col * this._cellW, y: ARENA.y + row * this._cellH, w: this._cellW, h: this._cellH });
    }
    return cells;
  }

  getVisual() {
    if (this.state === PatternState.TELEGRAPH) {
      return { kind: 'unmadeGround', cells: this._cells(), t: 1 - this._timer / this.telegraphDuration, active: false };
    }
    if (this.state === PatternState.ACTIVE) {
      return { kind: 'unmadeGround', cells: this._cells(), t: 1, active: true };
    }
    return null;
  }
}
